import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import {
    onPosIntegrationsLoad,
    onPosIntegrationsSave,
} from "../../store/Restaurant/actions";
import IntegrationList from "./IntegrationList";
import IikoTransport from "./iikoTransport";
import IikoBiz from "./iikoBiz";
import Button, { BtnColor } from "../../components/Button/Button";
import PulseLoader from "react-spinners/PulseLoader";
import Squareup from "./Squareup";
import { Form, Formik } from "formik";
import { useParams } from 'react-router-dom';
import { RoundedBox } from '../../components/Shared/RoundedBox';
import { useQueryClient } from '@tanstack/react-query';

export const POS = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { slug } = useParams();
    const queryClient = useQueryClient();

    // send request to get updated store settings from backend
    const invalidateStore = () => queryClient.invalidateQueries(['store', slug]);

    const [selectedIntegration, setIntegration] = useState({});
    const [params, setParams] = useState({});

    let integrations = useSelector(
        (state) => state.restaurant.pos_integrations
    );
    let loading = useSelector((state) => state.restaurant.loading);
    let posSelected = useSelector((state) => state.restaurant.pos_selected);

    const Integration = {
        Iikobiz: "iiko_biz",
        Iikotransport: "iiko_transport",
        Squareup: "squareup",
    };

    const showSave =
        selectedIntegration?.name === Integration.Iikobiz ||
        selectedIntegration?.name === Integration.Iikotransport ||
        selectedIntegration?.name === Integration.Squareup;

    const onSave = () => {
        let data = {};
        data.restaurant_id = slug;
        data.integration = selectedIntegration;
        data.integration.params = params;

        dispatch(onPosIntegrationsSave(data, invalidateStore));
    };

    useEffect(() => {
        dispatch(onPosIntegrationsLoad(slug));
    }, []);

    useEffect(() => {
        if (posSelected?.integration) {
            setIntegration(posSelected.integration);
        }
        if (posSelected?.params) {
            setParams({ ...params, ...posSelected.params });
        }
    }, [posSelected?.params, posSelected?.integration]);

    const setIikoUserId = (value) => setParams({ ...params, user_id: value });
    const setIikoSecret = (value) =>
        setParams({ ...params, user_secret: value });
    const setIikoOrgId = (value) =>
        setParams({ ...params, organization_id: value });
    const setApiLogin = (value) => setParams({ ...params, api_login: value });
    const setIikoServer = (value) => setParams({ ...params, server: value });
    const setSquareupAccessToken = (value) =>
        setParams({ ...params, access_token: value });
    const setLocationId = (value) =>
        setParams({ ...params, location_id: value });
    const setEnvironment = (value) =>
        setParams({ ...params, environment: value });

    return (
        <RoundedBox>
        <div className="w-full bg-white">
            <Formik
                enableReinitialize={true}
                initialValues={{
                    squareupAccessToken: params?.access_token || "",
                    locationId: params?.location_id || "",
                    environment: params?.environment || "",
                }}
                validate={(values) => {
                    let errors = {};

                    let validateSquareup = () => {
                        if (selectedIntegration?.name !== Integration.Squareup) {
                            return;
                        }

                        if (!values.squareupAccessToken) {
                            errors.squareupAccessToken = t("Required");
                        }
                        if (!values.locationId) {
                            errors.locationId = t("Required");
                        }
                        if (!values.environment) {
                            errors.environment = t("Required");
                        }
                    }
                    validateSquareup();

                    return errors;
                }}
                onSubmit={() => {
                    onSave();
                }}
            >
                {({ errors, touched, handleBlur, setFieldValue, values }) => (
                    <Form>
                        <div className="grid grid-cols-3 gap-4">
                            <IntegrationList
                                selected={selectedIntegration}
                                setSelected={setIntegration}
                                integrations={integrations}
                            />
                            {selectedIntegration?.name ===
                            Integration.Iikobiz ? (
                                <IikoBiz
                                    params={params}
                                    iikoUserId={params?.user_id}
                                    setIikoUserId={setIikoUserId}
                                    iikoSecret={params?.user_secret}
                                    setIikoSecret={setIikoSecret}
                                    iikoOrgId={params?.organization_id}
                                    setIikoOrgId={setIikoOrgId}
                                />
                            ) : null}

                            {selectedIntegration?.name ===
                            Integration.Iikotransport ? (
                                <IikoTransport
                                    params={params}
                                    value={params?.api_login}
                                    setValue={setApiLogin}
                                    setIikoServer={setIikoServer}
                                />
                            ) : null}
                            {selectedIntegration?.name ===
                            Integration.Squareup ? (
                                <Squareup
                                    params={params}
                                    squareupAccessToken={params?.access_token}
                                    setSquareupAccessToken={
                                        setSquareupAccessToken
                                    }
                                    locationId={params?.location_id}
                                    setLocationId={setLocationId}
                                    environment={params?.environment}
                                    setEnvironment={setEnvironment}
                                    errors={errors}
                                    touched={touched}
                                    handleChange={setFieldValue}
                                    handleBlur={handleBlur}
                                />
                            ) : null}
                        </div>
                        {showSave ? (
                            <div className="py-3">
                                <Button color={BtnColor.indigo} type="submit">
                                    {loading ? (
                                        <PulseLoader
                                            color="rgba(79, 70, 229, 1)"
                                            loading={loading}
                                            size={5}
                                        />
                                    ) : (
                                        t("Save")
                                    )}
                                </Button>
                            </div>
                        ) : null}
                    </Form>
                )}
            </Formik>
        </div>
        </RoundedBox>
    );
};
